import { useEffect, useState } from "react";
import { Category, manageCategoryApi } from "../../../../../entities/category";
import { Keyword, manageKeywordApi } from "../../../../../entities/keyword";
import { manageProductApi } from "../../../../../entities/product";
import { manageAuthorApi } from "../../../../../entities/author";
import {
  ArticleImage,
  ArticlePutReqDTO,
  manageArticleApi,
} from "../../../../../entities/article";
import { ArticleImagePutReqDTO } from "../../../../../entities/article/api/article-dto";
import { getApiClient } from "../../../../../shared/api/client.api";
import { DATA_SOURCE } from "../../../../../types/enum";

export interface ModifyArticleFormData {
  titleKo: string;
  titleEn: string;
  contentKo: string;
  contentEn: string;
  author: {
    id: string;
    authorName: string;
    authorEmail?: string;
    authorInsta?: string;
    authorLinkedin?: string;
    numOfArticles?: number;
  };
  keyword: {
    id: string;
    nameKo: string;
    descKo: string;
    descEn: string;
    categoryId: string;
    categoryNameKo: string;
  };
  product?: {
    id: string;
    nameKo: string;
    nameEn?: string;
    imageUrl?: string;
    numOfArticles?: number;
  };
  images: ArticleImage[];
  studyLinks: string[];
  selectedCategory: string | undefined;
  uxEvaluationTag: string;
  dataSource: string;
}

export const useModifyArticle = (articleId: string) => {
  const [formData, setFormData] = useState<ModifyArticleFormData>({
    titleKo: "",
    titleEn: "",
    contentKo: "",
    contentEn: "",
    images: [{ imageUrl: "", descKo: "", descEn: "", order: 0 }],
    studyLinks: [""],
    author: {
      id: "",
      authorName: "",
      authorEmail: "",
      authorInsta: "",
      authorLinkedin: "",
    },
    keyword: {
      id: "",
      nameKo: "",
      descKo: "",
      descEn: "",
      categoryId: "",
      categoryNameKo: "",
    },
    product: {
      id: "",
      nameKo: "",
      nameEn: "",
      imageUrl: "",
    },
    selectedCategory: "",
    uxEvaluationTag: "",
    dataSource: DATA_SOURCE.CUSTOM,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [availableKeywords, setAvailableKeywords] = useState<Keyword[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [authors, setAuthors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [uploadingImages, setUploadingImages] = useState<Set<number>>(
    new Set(),
  );

  // 기존 아티클 데이터 로드
  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        const article = await manageArticleApi.getArticle(articleId);

        if (article) {
          setFormData({
            titleKo: article.titleKo,
            titleEn: article.titleEn || "",
            contentKo: article.contentKo,
            contentEn: article.contentEn || "",
            keyword: article.keyword,
            product: article.product,
            author: article.author,
            selectedCategory: article.keyword.categoryId,
            images: article.images || [
              { imageUrl: "", descKo: "", descEn: "", order: 0 },
            ],

            studyLinks: article.studyLinks || [""],

            uxEvaluationTag: article.uxEvaluation || "",
            dataSource: article.dataSource || DATA_SOURCE.CUSTOM,
          });
        }
      } catch (err) {
        setError("아티클을 불러오는데 실패했습니다.");
        console.error("Failed to load article:", err);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleId]);

  // 카테고리, 키워드, 제품, 작성자 목록 로드
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [categoriesData, keywordsData, productsData, authorsData] =
          await Promise.all([
            manageCategoryApi.getCategoryList(),
            manageKeywordApi.getKeywordList(),
            manageProductApi.getProductList(),
            manageAuthorApi.getAuthorList(),
          ]);

        setCategories(categoriesData);
        setAvailableKeywords(keywordsData);
        setProducts(productsData);
        setAuthors(authorsData);

        // 기존 키워드의 카테고리 ID 설정
        if (formData.keyword.id) {
          const keyword = keywordsData.find(
            (k) => k.id === formData.keyword.id,
          );
          if (keyword) {
            setFormData((prev) => ({
              ...prev,
              selectedCategory: keyword.categoryId,
            }));
          }
        }
      } catch (err) {
        setError("데이터를 불러오는데 실패했습니다.");
        console.error("Failed to load initial data:", err);
      }
    };

    loadInitialData();
  }, [formData.keyword.id]);

  // 카테고리 변경 시 키워드 목록 업데이트
  useEffect(() => {
    const updateKeywords = async () => {
      if (formData.selectedCategory) {
        try {
          const keywords = await manageKeywordApi.getKeywordList();
          setAvailableKeywords(
            keywords.filter((k) => k.categoryId === formData.selectedCategory),
          );
        } catch (err) {
          console.error("Failed to load keywords:", err);
        }
      }
    };

    updateKeywords();
  }, [formData.selectedCategory]);

  // 폼 데이터 업데이트 함수들
  const updateFormData = (updates: Partial<ModifyArticleFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleCategoryChange = (categoryId: string) => {
    updateFormData({
      selectedCategory: categoryId,
      // 선택된 카테고리에 맞지 않는 키워드 초기화
      keyword: {
        id: "",
        nameKo: "",
        descKo: "",
        descEn: "",
        categoryId: "",
        categoryNameKo: "",
      },
    });
  };

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    updateFormData({
      product: product,
    });
  };

  const handleKeywordChange = (keyword: Keyword) => {
    updateFormData({
      keyword: {
        id: keyword.id,
        nameKo: keyword.nameKo,
        descKo: keyword.descKo,
        descEn: keyword.descEn,
        categoryId: keyword.categoryId,
        categoryNameKo: keyword.categoryNameKo || "",
      },
      titleKo: keyword.nameKo,
      titleEn: keyword.nameEn,
      contentKo: keyword.descKo,
      contentEn: keyword.descEn,
      studyLinks: keyword.studyLinks || [""],
    });
  };

  const handleAuthorChange = (author: any) => {
    updateFormData({
      author: {
        id: author.id,
        authorName: author.authorName,
        authorEmail: author.authorEmail,
        authorInsta: author.authorInsta,
        authorLinkedin: author.authorLinkedin,
        numOfArticles: author.numOfArticles,
      },
    });
  };

  const handleUxEvaluationChange = (uxEvaluation: string) => {
    updateFormData({ uxEvaluationTag: uxEvaluation });
  };

  const handleDataSourceChange = (dataSource: string) => {
    updateFormData({ dataSource });
  };

  // 아티클 링크 관리
  const addStudyLink = () => {
    updateFormData({
      studyLinks: [...formData.studyLinks, ""],
    });
  };

  const removeStudyLink = (index: number) => {
    updateFormData({
      studyLinks: formData.studyLinks.filter((_, i) => i !== index),
    });
  };

  const updateStudyLink = (index: number, value: string) => {
    const newLinks = [...formData.studyLinks];
    newLinks[index] = value;
    updateFormData({ studyLinks: newLinks });
  };

  // 이미지 업로드 함수
  const uploadImageToS3 = async (file: File): Promise<string> => {
    const apiClient = getApiClient();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "article");

    const response = await apiClient.post("/upload/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.data.success) {
      return response.data.data.url;
    } else {
      throw new Error("이미지 업로드에 실패했습니다.");
    }
  };

  // 이미지 관리
  const addImage = () => {
    const newOrder =
      Math.max(...formData.images.map((img) => img.order), -1) + 1;
    updateFormData({
      images: [
        ...formData.images,
        { imageUrl: "", descKo: "", descEn: "", order: newOrder },
      ],
    });
  };

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    // order 재정렬
    const reorderedImages = newImages.map((img, idx) => ({
      ...img,
      order: idx,
    }));
    updateFormData({ images: reorderedImages });
  };

  const updateImage = (
    index: number,
    field: keyof ArticleImage,
    value: string,
  ) => {
    const newImages = [...formData.images];
    newImages[index] = { ...newImages[index], [field]: value };
    updateFormData({ images: newImages });
  };

  // 이미지 파일 업로드 처리
  const handleImageFileUpload = async (index: number, file: File) => {
    try {
      setUploadingImages((prev) => new Set(prev).add(index));
      const imageUrl = await uploadImageToS3(file);
      updateImage(index, "imageUrl", imageUrl);
    } catch (error) {
      console.error("Failed to upload image:", error);
      setError("이미지 업로드에 실패했습니다.");
    } finally {
      setUploadingImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  // 이미지 순서 변경
  const moveImageUp = (index: number) => {
    if (index > 0) {
      const newImages = [...formData.images];
      [newImages[index - 1], newImages[index]] = [
        newImages[index],
        newImages[index - 1],
      ];
      // order 재정렬
      const reorderedImages = newImages.map((img, idx) => ({
        ...img,
        order: idx,
      }));
      updateFormData({ images: reorderedImages });
    }
  };

  const moveImageDown = (index: number) => {
    if (index < formData.images.length - 1) {
      const newImages = [...formData.images];
      [newImages[index], newImages[index + 1]] = [
        newImages[index + 1],
        newImages[index],
      ];
      // order 재정렬
      const reorderedImages = newImages.map((img, idx) => ({
        ...img,
        order: idx,
      }));
      updateFormData({ images: reorderedImages });
    }
  };

  // ArticleImage를 ArticleImagePutReqDTO로 변환
  const convertImagesToPutDTO = (
    images: ArticleImage[],
  ): ArticleImagePutReqDTO[] => {
    return images.map((img) => ({
      imageUrl: img.imageUrl,
      descKo: img.descKo || "",
      descEn: img.descEn || "",
      order: img.order,
    }));
  };

  // 필수 항목 검증
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // 제목(한국어) 검증
    if (!formData.titleKo || formData.titleKo.trim() === "") {
      errors.titleKo = "제목(한국어)을 입력해주세요.";
    }

    // 내용(한국어) 검증
    if (!formData.contentKo || formData.contentKo.trim() === "") {
      errors.contentKo = "내용(한국어)을 입력해주세요.";
    }

    // 키워드 검증
    if (!formData.keyword.id || formData.keyword.id.trim() === "") {
      errors.keyword = "키워드를 선택해주세요.";
    }

    // 제품 검증
    if (!formData.product?.id || formData.product.id.trim() === "") {
      errors.product = "제품을 선택해주세요.";
    }

    setValidationErrors(errors);

    // 에러가 있으면 첫 번째 필드로 스크롤
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const fieldElement = document.querySelector(
        `[data-field="${firstErrorField}"]`,
      );
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        // 포커스 가능한 요소면 포커스
        const focusableElement = fieldElement.querySelector(
          "input, textarea, select",
        ) as HTMLElement;
        if (focusableElement) {
          setTimeout(() => focusableElement.focus(), 300);
        }
      }
      return false;
    }

    return true;
  };

  // 아티클 수정 제출
  const submitArticle = async () => {
    // 검증 실패 시 제출 중단
    if (!validateForm()) {
      return false;
    }

    // 검증 성공 시 에러 초기화
    setValidationErrors({});
    setError(null);

    try {
      const articleData: ArticlePutReqDTO = {
        titleKo: formData.titleKo.trim(),
        titleEn: formData.titleEn.trim(),
        contentKo: formData.contentKo.trim(),
        contentEn: formData.contentEn.trim(),
        keywordId: formData.keyword.id,
        uxEvaluation: formData.uxEvaluationTag,
        dataSource: formData.dataSource,
        images: convertImagesToPutDTO(formData.images),
        authorId: formData.author.id,
        studyLinks: formData.studyLinks.filter((link) => link.trim() !== ""),
        productId: formData.product?.id,
      };

      await manageArticleApi.updateArticle(articleId, articleData);
      return true;
    } catch (err: any) {
      // 백엔드에서 반환한 에러 메시지 처리
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "아티클 수정에 실패했습니다.";
      setError(errorMessage);
      console.error("Failed to update article:", err);
      return false;
    }
  };

  return {
    formData,
    categories,
    availableKeywords,
    products,
    authors,
    loading,
    error,
    validationErrors,
    uploadingImages,
    updateFormData,
    handleCategoryChange,
    handleProductChange,
    handleKeywordChange,
    handleAuthorChange,
    handleUxEvaluationChange,
    handleDataSourceChange,
    addStudyLink,
    removeStudyLink,
    updateStudyLink,
    addImage,
    removeImage,
    updateImage,
    handleImageFileUpload,
    moveImageUp,
    moveImageDown,
    submitArticle,
  };
};
